import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconArrowLeft, IconCalendar } from "@tabler/icons-react";
import { RefreshCw } from "lucide-react";
import { NotificationFilterState } from "../types/type";
import { FILTER, NOTIFICATIONS } from "../constants/drawer.constant";
import DrawerLayout from "./DrawerLayout";

interface DrawerProps {
  activeDrawer: any;
  setActiveDrawer: any;
  filterState: NotificationFilterState;
  setFilterState: any;
}
const FilterDrawer = ({
  activeDrawer,
  setActiveDrawer,
  filterState,
  setFilterState,
}: DrawerProps) => {
  const { t } = useTranslation();
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef   = useRef<HTMLInputElement>(null);

  // Default both pickers to today when the filter drawer is first opened
  // and no dates have been chosen yet.
  useEffect(() => {
    if (activeDrawer === FILTER && !filterState.dateRange.from && !filterState.dateRange.to) {
      const today = new Date();
      setFilterState((prev: NotificationFilterState) => ({
        ...prev,
        dateRange: { from: today, to: today },
      }));
    }
  }, [activeDrawer]); // eslint-disable-line react-hooks/exhaustive-deps
  const title = (
    <Group
      px="md"
      style={{
        borderBottom: "1px solid #F0F2F7",
        position: "sticky",
        top: 0,
        backgroundColor: "#FDFDFD",
        zIndex: 100,
        height: "64px",
        color: "#0D0D0D",
      }}
    >
      <UnstyledButton onClick={() => setActiveDrawer(NOTIFICATIONS)}>
        <IconArrowLeft size={24} stroke={1.5} />
      </UnstyledButton>
      <Text size="lg" fw={500}>
        {t("FILTER_NOTIFICATIONS")}
      </Text>
    </Group>
  );
  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "20px 0",
      }}
    >
      <Stack spacing="xs">
        {/* From Date */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Text
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#5E656B",
            }}
          >
            {t("FROM_DATE")}
          </Text>
          <DateInput
            ref={fromRef}
            value={filterState.dateRange.from}
            onChange={(date) => {
              // If fromDate moves past toDate, reset toDate
              const newTo =
                date && filterState.dateRange.to && date > filterState.dateRange.to
                  ? date
                  : filterState.dateRange.to;
              setFilterState({
                ...filterState,
                dateRange: { from: date, to: newTo },
              });
            }}
            maxDate={filterState.dateRange.to ?? undefined}
            placeholder={t<string>("SELECT_DATE")}
            valueFormat="DD/MM/YYYY"
            rightSection={
              <IconCalendar
                size={20}
                style={{ color: "#94A3B8", cursor: "pointer" }}
                onClick={() => fromRef.current?.focus()}
              />
            }
            styles={{ rightSection: { pointerEvents: "all" } }}
            w="100%"
            sx={(theme) => ({
              flex: 1,
              "& input": {
                height: "44px",
                fontSize: "15px",
                "&::placeholder": {
                  color: theme.colors.gray[6],
                },
                "&:focus": {
                  borderColor: "#2A81D0",
                },
              },
              "& .mantine-TextInput-wrapper": {
                height: "44px",
              },
              "& .mantine-TextInput-input": {
                borderColor: "#D8DDE2",
                borderRadius: "10px",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#2A81D0",
                },
              },
            })}
          />
        </Box>

        {/* To Date */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Text
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#5E656B",
            }}
          >
            {t("TO_DATE")}
          </Text>
          <DateInput
            ref={toRef}
            value={filterState.dateRange.to}
            onChange={(date) =>
              setFilterState({
                ...filterState,
                dateRange: { ...filterState.dateRange, to: date },
              })
            }
            minDate={filterState.dateRange.from ?? undefined}
            placeholder={t<string>("SELECT_DATE")}
            valueFormat="DD/MM/YYYY"
            rightSection={
              <IconCalendar
                size={20}
                style={{ color: "#94A3B8", cursor: "pointer" }}
                onClick={() => toRef.current?.focus()}
              />
            }
            styles={{ rightSection: { pointerEvents: "all" } }}
            w="100%"
            sx={(theme) => ({
              flex: 1,
              "& input": {
                height: "44px",
                fontSize: "15px",
                "&::placeholder": {
                  color: theme.colors.gray[6],
                },
                "&:focus": {
                  borderColor: "#2A81D0",
                },
              },
              "& .mantine-TextInput-wrapper": {
                height: "44px",
              },
              "& .mantine-TextInput-input": {
                borderColor: "#D8DDE2",
                borderRadius: "10px",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#2A81D0",
                },
              },
            })}
          />
        </Box>
      </Stack>
    </Box>
  );
  const handleReset = () => {
    const today = new Date();
    setFilterState((prev: NotificationFilterState) => ({
      ...prev,
      dateRange: { from: today, to: today },
    }));
  };

  const handleApply = () => {
    // Navigate back to the main notifications drawer so NotificationListPersonal
    // re-renders with the updated dateRange from filterState.
    setActiveDrawer(NOTIFICATIONS);
  };

  const bottom = (
    <Group
      sx={{
        justifyContent: "flex-end",
        gap: "md",
        fontSize: "14px",
        fontWeight: 700,
      }}
    >
      <Button
        onClick={handleReset}
        sx={{
          backgroundColor: "white",
          color: "#2A81D0",
          width: "44px",
          height: "44px",
          borderRadius: "8px",
          border: "1px solid #2A81D0",
          padding: 0,
          "&:hover": { backgroundColor: "initial", borderColor: "initial" },
        }}
      >
        <RefreshCw size={24} />
      </Button>
      <Button
        onClick={handleApply}
        sx={{
          backgroundColor: "#2A81D0",
          color: "white",
          padding: "10px 24px",
          borderRadius: "8px",
          height: "44px",
          "&:hover": { backgroundColor: "#2A81D0" },
        }}
      >
        {t("APPLY")}
      </Button>
    </Group>
  );
  return (
    <DrawerLayout
      opened={activeDrawer === FILTER}
      onClose={() => setActiveDrawer(NOTIFICATIONS)}
      title={title}
      content={content}
      bottom={bottom}
    />
  );
};

export default FilterDrawer;
